/**
 * The collage board's pointer rules as a pure state machine, and its tap rules
 * as a pure decision — so "tap A, then tap B", long-press drag, pinch and pan
 * are tested without a browser, and the DOM code only wires events to them.
 *
 * Pointer rules (docs/photo-collage.md §7, design §1.2):
 *  - a pointer down starts a PRESS on whatever it landed on (a slot, a spare,
 *    the sheet's background);
 *  - moving more than 8 px turns the press into a PAN (a mouse on a photo
 *    starts a DRAG after 5 px instead: desktop drag-and-drop);
 *  - holding 420 ms still on touch over a photo LIFTS it for a drag (the board
 *    arms the timer the machine asks for and calls `longPress` when it fires);
 *  - a second pointer cancels any press or drag and starts a PINCH about the
 *    midpoint; lifting one finger of a pinch pans on with the other;
 *  - a press released without moving is a TAP; two taps on the background
 *    within 300 ms and 30 px are a DOUBLE TAP (zoom 1 ↔ 2.5 about the point).
 *
 * Times are passed in (ms, any monotonic clock); the machine holds no timers.
 */

/** What a pointer can land on. A slot may be filled or empty; the board knows which. */
export type HitTarget =
  | { kind: 'slot'; id: string }
  | { kind: 'spare'; id: string }
  | { kind: 'background' };

export type PointerKind = 'touch' | 'mouse' | 'pen';

export const TAP_SLOP = 8;
export const MOUSE_DRAG_SLOP = 5;
export const LONG_PRESS_MS = 420;
export const DOUBLE_TAP_MS = 300;
export const DOUBLE_TAP_SLOP = 30;

export interface GesturePoint {
  x: number;
  y: number;
}

export type GestureAction =
  /** A tap on a target (the tap rules decide what it means). */
  | { type: 'tap'; target: HitTarget; point: GesturePoint }
  /** Two quick taps on the background: toggle the zoom about the point. */
  | { type: 'doubleTap'; point: GesturePoint }
  /** Arm a timer; call `longPress(pointerId, time)` when it fires. */
  | { type: 'armLongPress'; pointerId: number; ms: number }
  /** The long-press timer is no longer wanted. */
  | { type: 'disarmLongPress' }
  /** A pan, pinch or drag began: hide the detail render, stop animations. */
  | { type: 'gestureStart' }
  /** Every finger is up (or the gesture was cancelled): settle the view, render detail. */
  | { type: 'gestureEnd' }
  /** Move the sheet by this much (stage px). */
  | { type: 'pan'; dx: number; dy: number }
  /** Zoom by `factor` about `center` (stage px), then move by `dx`,`dy`. */
  | { type: 'pinch'; factor: number; center: GesturePoint; dx: number; dy: number }
  /** A photo was lifted (long press, or a mouse dragging it). */
  | { type: 'lift'; from: HitTarget; point: GesturePoint; pointer: PointerKind }
  /** The lifted photo moved. */
  | { type: 'dragMove'; point: GesturePoint }
  /** The lifted photo was let go over `point`; the board finds what is under it. */
  | { type: 'drop'; from: HitTarget; point: GesturePoint }
  /** The drag ended without a drop (a second finger, a cancel). */
  | { type: 'dragCancel' };

type State =
  | { kind: 'idle' }
  | {
      kind: 'press';
      pointerId: number;
      pointer: PointerKind;
      target: HitTarget;
      liftable: boolean;
      start: GesturePoint;
      last: GesturePoint;
      at: number;
    }
  | { kind: 'pan'; pointerId: number; last: GesturePoint }
  | { kind: 'pinch'; a: number; b: number; dist: number; mid: GesturePoint }
  | { kind: 'drag'; pointerId: number; from: HitTarget; last: GesturePoint }
  /** After a cancel: every pointer must lift before anything new starts. */
  | { kind: 'spent' };

export interface GestureOptions {
  /** Whether a target holds a photo that can be lifted (a filled slot or a spare). */
  liftable(target: HitTarget): boolean;
}

export class GestureMachine {
  private state: State = { kind: 'idle' };
  private readonly points = new Map<number, GesturePoint>();
  private lastTap: { at: number; point: GesturePoint } | undefined;

  constructor(private readonly options: GestureOptions) {}

  /** For tests and for the board's "is a finger down" checks. */
  get mode(): State['kind'] {
    return this.state.kind;
  }

  get dragging(): boolean {
    return this.state.kind === 'drag';
  }

  down(pointerId: number, pointer: PointerKind, point: GesturePoint, target: HitTarget, time: number): GestureAction[] {
    this.points.set(pointerId, point);
    const out: GestureAction[] = [];
    if (this.points.size >= 2) {
      // A second finger: whatever the first one was doing becomes a pinch.
      const s = this.state;
      if (s.kind === 'press') out.push({ type: 'disarmLongPress' });
      if (s.kind === 'drag') out.push({ type: 'dragCancel' });
      if (s.kind !== 'pinch' && s.kind !== 'pan') out.push({ type: 'gestureStart' });
      const [a, b] = [...this.points.keys()] as [number, number];
      const pa = this.points.get(a)!;
      const pb = this.points.get(b)!;
      this.state = { kind: 'pinch', a, b, dist: distance(pa, pb), mid: midpoint(pa, pb) };
      this.lastTap = undefined;
      return out;
    }
    const liftable = this.options.liftable(target);
    this.state = { kind: 'press', pointerId, pointer, target, liftable, start: point, last: point, at: time };
    if (pointer !== 'mouse' && liftable) out.push({ type: 'armLongPress', pointerId, ms: LONG_PRESS_MS });
    return out;
  }

  move(pointerId: number, point: GesturePoint): GestureAction[] {
    if (!this.points.has(pointerId)) return [];
    this.points.set(pointerId, point);
    const s = this.state;
    if (s.kind === 'press' && s.pointerId === pointerId) {
      const moved = distance(point, s.start);
      if (s.pointer === 'mouse' && s.liftable && moved > MOUSE_DRAG_SLOP) {
        this.state = { kind: 'drag', pointerId, from: s.target, last: point };
        return [
          { type: 'gestureStart' },
          { type: 'lift', from: s.target, point: s.start, pointer: s.pointer },
          { type: 'dragMove', point },
        ];
      }
      if (moved > TAP_SLOP) {
        this.state = { kind: 'pan', pointerId, last: point };
        this.lastTap = undefined;
        return [
          { type: 'disarmLongPress' },
          { type: 'gestureStart' },
          { type: 'pan', dx: point.x - s.start.x, dy: point.y - s.start.y },
        ];
      }
      s.last = point;
      return [];
    }
    if (s.kind === 'pan' && s.pointerId === pointerId) {
      const dx = point.x - s.last.x;
      const dy = point.y - s.last.y;
      s.last = point;
      return dx || dy ? [{ type: 'pan', dx, dy }] : [];
    }
    if (s.kind === 'drag' && s.pointerId === pointerId) {
      s.last = point;
      return [{ type: 'dragMove', point }];
    }
    if (s.kind === 'pinch' && (pointerId === s.a || pointerId === s.b)) {
      const pa = this.points.get(s.a);
      const pb = this.points.get(s.b);
      if (!pa || !pb) return [];
      const dist = distance(pa, pb);
      const mid = midpoint(pa, pb);
      const factor = s.dist > 0 && dist > 0 ? dist / s.dist : 1;
      const action: GestureAction = { type: 'pinch', factor, center: s.mid, dx: mid.x - s.mid.x, dy: mid.y - s.mid.y };
      s.dist = dist;
      s.mid = mid;
      return [action];
    }
    return [];
  }

  /** The long-press timer fired. */
  longPress(pointerId: number, _time: number): GestureAction[] {
    const s = this.state;
    if (s.kind !== 'press' || s.pointerId !== pointerId || !s.liftable || s.pointer === 'mouse') return [];
    if (distance(s.last, s.start) > TAP_SLOP) return [];
    this.state = { kind: 'drag', pointerId, from: s.target, last: s.last };
    this.lastTap = undefined;
    return [{ type: 'gestureStart' }, { type: 'lift', from: s.target, point: s.last, pointer: s.pointer }];
  }

  up(pointerId: number, point: GesturePoint, time: number): GestureAction[] {
    if (!this.points.has(pointerId)) return [];
    this.points.set(pointerId, point);
    const s = this.state;
    this.points.delete(pointerId);
    if (s.kind === 'press' && s.pointerId === pointerId) {
      this.state = { kind: 'idle' };
      const out: GestureAction[] = [{ type: 'disarmLongPress' }];
      if (s.target.kind === 'background') {
        const last = this.lastTap;
        if (last && time - last.at <= DOUBLE_TAP_MS && distance(last.point, point) <= DOUBLE_TAP_SLOP) {
          this.lastTap = undefined;
          out.push({ type: 'doubleTap', point });
          return out;
        }
        this.lastTap = { at: time, point };
      } else {
        this.lastTap = undefined;
      }
      out.push({ type: 'tap', target: s.target, point });
      return out;
    }
    if (s.kind === 'drag' && s.pointerId === pointerId) {
      this.state = this.points.size > 0 ? { kind: 'spent' } : { kind: 'idle' };
      return [{ type: 'drop', from: s.from, point }, { type: 'gestureEnd' }];
    }
    if (s.kind === 'pan' && s.pointerId === pointerId) {
      this.state = this.points.size > 0 ? { kind: 'spent' } : { kind: 'idle' };
      return [{ type: 'gestureEnd' }];
    }
    if (s.kind === 'pinch' && (pointerId === s.a || pointerId === s.b)) {
      // One finger of a pinch lifted: the other one pans on.
      const other = pointerId === s.a ? s.b : s.a;
      const at = this.points.get(other);
      if (at) {
        this.state = { kind: 'pan', pointerId: other, last: at };
        return [];
      }
      this.state = { kind: 'idle' };
      return [{ type: 'gestureEnd' }];
    }
    // A finger left over from a cancelled gesture: nothing starts until all are up.
    if (this.points.size === 0) this.state = { kind: 'idle' };
    return [];
  }

  /** A pointer the browser took away (a system gesture, a lost capture). */
  cancel(pointerId: number): GestureAction[] {
    if (!this.points.has(pointerId)) return [];
    this.points.delete(pointerId);
    const s = this.state;
    const out: GestureAction[] = [];
    if (s.kind === 'press') out.push({ type: 'disarmLongPress' });
    if (s.kind === 'drag') out.push({ type: 'dragCancel' });
    if (s.kind === 'pan' || s.kind === 'pinch' || s.kind === 'drag') out.push({ type: 'gestureEnd' });
    this.state = this.points.size > 0 ? { kind: 'spent' } : { kind: 'idle' };
    this.lastTap = undefined;
    return out;
  }

  /** Forget everything (the board closed, the framer opened). */
  reset(): GestureAction[] {
    const s = this.state;
    const out: GestureAction[] = [];
    if (s.kind === 'press') out.push({ type: 'disarmLongPress' });
    if (s.kind === 'drag') out.push({ type: 'dragCancel' });
    if (s.kind === 'pan' || s.kind === 'pinch' || s.kind === 'drag') out.push({ type: 'gestureEnd' });
    this.points.clear();
    this.state = { kind: 'idle' };
    this.lastTap = undefined;
    return out;
  }
}

function distance(a: GesturePoint, b: GesturePoint): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function midpoint(a: GesturePoint, b: GesturePoint): GesturePoint {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

// ── Tap rules ────────────────────────────────────────────────────────────────

/** What is selected on the board: one slot or one spare. */
export type BoardSelection = { kind: 'slot'; id: string } | { kind: 'spare'; id: string };

export type TapDecision =
  | { type: 'none' }
  | { type: 'select'; target: BoardSelection }
  | { type: 'deselect' }
  /** The selected slot was tapped again and holds a photo that may be framed. */
  | { type: 'frame'; slot: string }
  /** Exchange the two: swap, move or bring a spare in. */
  | { type: 'exchange'; a: BoardSelection; b: BoardSelection };

/**
 * The board's tap table (docs/photo-collage.md §7). A tap on a placed photo
 * never moves one: tapping photo after photo — the natural way to look at and
 * frame each — only selects. Two photos change places when the shopper asked
 * for it: "Zameni mesto" first (`swapping`), or a long press dragged onto
 * another place (the drop, not a tap).
 *
 * | selected            | tap on                           | result                       |
 * |---------------------|----------------------------------|------------------------------|
 * | nothing             | slot or spare                    | select it                    |
 * | X                   | X, a filled slot                 | open the framer (if it may be framed; else deselect) |
 * | X                   | X, an empty slot or spare        | deselect                     |
 * | a spare             | a slot                           | put the spare there          |
 * | an empty slot       | a spare                          | put the spare there          |
 * | X                   | any other slot or spare Y        | select Y                     |
 * | X, swapping         | another slot or spare Y          | exchange X and Y             |
 * | X, swapping         | X                                | select X (stop swapping)     |
 * | anything            | the background                   | deselect                     |
 *
 * A spare is only ever selected to be put somewhere, and an empty slot to be
 * filled, so those taps place a photo without "Zameni mesto".
 */
export function decideTap(
  selected: BoardSelection | null,
  tapped: HitTarget,
  facts: { filled(slotId: string): boolean; frameable(slotId: string): boolean },
  swapping = false,
): TapDecision {
  if (tapped.kind === 'background') return selected ? { type: 'deselect' } : { type: 'none' };
  const target: BoardSelection = tapped.kind === 'slot' ? { kind: 'slot', id: tapped.id } : { kind: 'spare', id: tapped.id };
  if (!selected) return { type: 'select', target };
  if (selected.kind === target.kind && selected.id === target.id) {
    if (swapping) return { type: 'select', target };
    if (target.kind === 'slot' && facts.filled(target.id) && facts.frameable(target.id)) return { type: 'frame', slot: target.id };
    return { type: 'deselect' };
  }
  const emptyA = selected.kind === 'slot' && !facts.filled(selected.id);
  const emptyB = target.kind === 'slot' && !facts.filled(target.id);
  if (selected.kind === 'spare' && target.kind === 'spare') return { type: 'select', target };
  if (emptyA && emptyB) return { type: 'select', target };
  if (swapping) return { type: 'exchange', a: selected, b: target };
  if (selected.kind === 'spare' && target.kind === 'slot') return { type: 'exchange', a: selected, b: target };
  if (emptyA && target.kind === 'spare') return { type: 'exchange', a: selected, b: target };
  return { type: 'select', target };
}
